import {Badge, Box, Button, createStyles, Group, SegmentedControl, Stack, Text, Title} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {InAppNotification, NotificationTab} from './InAppNotificationConstants';
import NotificationRow from './NotificationRow';

// The panel is a fixed size regardless of notification count; a short list leaves blank space rather
// than shrinking the panel.
const PANEL_WIDTH = 400;
const PANEL_HEIGHT = 560;
// Diameter of the circular icon badge shown in the empty and error states.
const EMPTY_ICON_SIZE_PX = 56;

const useStyles = createStyles((theme) => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    width: PANEL_WIDTH,
    // Cap the height at the viewport less a small gutter (`3xl` = 32px) so the panel never runs off
    // screen on short viewports.
    height: `min(${PANEL_HEIGHT}px, calc(100vh - ${theme.spacing['3xl']}))`,
    // Round the panel to match the enclosing popover's `radius="lg"` so `overflow: hidden` clips the
    // scrolling list along the curve; without it, rows overlap the dropdown's rounded corners.
    borderRadius: theme.radius.lg,
    overflow: 'hidden',
  },
  header: {
    padding: `${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.xs}`,
  },
  tabRow: {
    padding: `${theme.spacing.sm} ${theme.spacing.lg} ${theme.spacing.md}`,
  },
  segmentedControl: {
    height: 'auto',
    display: 'flex',
  },
  segmentedControlLabel: {
    height: 'auto',
    overflow: 'visible',
    flexGrow: 1,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
  listArea: {
    flex: 1,
    overflowY: 'auto',
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    backgroundColor: theme.colors.gray[0],
    position: 'relative',
  },
  // Floats over the top of the list so new arrivals are announced without shifting the rows. Centered
  // horizontally and pinned just below the list's top edge; stays put as the list scrolls under it.
  newPill: {
    position: 'sticky',
    top: theme.spacing.sm,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'center',
    height: 0,
    pointerEvents: 'none',
  },
  newPillButton: {
    pointerEvents: 'auto',
    boxShadow: theme.shadows.sm,
  },
  loadingMore: {
    padding: theme.spacing.md,
  },
  // Closes the list once every page is loaded, so the end of the feed reads as intentional rather
  // than a list that merely stopped scrolling.
  endOfList: {
    padding: theme.spacing.md,
    textAlign: 'center',
    backgroundColor: theme.colors.gray[0],
  },
  emptyState: {
    height: '100%',
  },
  emptyIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: `${EMPTY_ICON_SIZE_PX}px`,
    height: `${EMPTY_ICON_SIZE_PX}px`,
    borderRadius: '50%',
    backgroundColor: theme.white,
    color: theme.colors.blue[6],
    boxShadow: theme.shadows.sm,
    // Nucleus `Icon` has no `size` prop; its glyph is sized via CSS `font-size`.
    fontSize: '24px',
  },
}));

interface EmptyStateProps {
  activeTab: NotificationTab;
}

function EmptyState({activeTab}: EmptyStateProps): JSX.Element {
  const {classes} = useStyles();
  const isUnreadTab = activeTab === NotificationTab.UNREAD;
  return (
    <Stack
      align="center"
      justify="center"
      spacing="md"
      className={classes.emptyState}
      data-amp-safe={true}
      {...getTestProps({component: 'NotificationPanel', element: 'emptyState', key: activeTab})}
    >
      <Box className={classes.emptyIcon}>
        <BenchlingIcon icon={isUnreadTab ? 'check-circle' : 'bell'} />
      </Box>
      <Stack align="center" spacing="xs">
        <Text weight={600}>You're all caught up!</Text>
        <Text size="sm" color="dimmed">
          {isUnreadTab ? 'No unread notifications' : 'No notifications'}
        </Text>
      </Stack>
    </Stack>
  );
}

interface ErrorStateProps {
  onRetry: () => void;
}

function ErrorState({onRetry}: ErrorStateProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <Stack
      align="center"
      justify="center"
      spacing="md"
      className={classes.emptyState}
      {...getTestProps({component: 'NotificationPanel', element: 'errorState'})}
    >
      <Box className={classes.emptyIcon}>
        <BenchlingIcon icon="warning" />
      </Box>
      <Stack align="center" spacing="xs">
        <Text weight={600}>Couldn't load notifications</Text>
        <Text size="sm" color="dimmed">
          Something went wrong. Try again.
        </Text>
      </Stack>
      <Button variant="ghost" size="xs" onClick={onRetry}>
        Retry
      </Button>
    </Stack>
  );
}

export interface NotificationPanelProps {
  activeTab: NotificationTab;
  onTabChange: (tab: NotificationTab) => void;
  notifications: Array<InAppNotification>;
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  // True when the active tab has older pages left to fetch. Drives the scroll-to-bottom trigger.
  hasMore: boolean;
  // Fetches and appends the active tab's next page. Fire-and-forget; the panel tracks its own
  // in-flight state to show the bottom throbber since the hook's `loadMore` returns void.
  onLoadMore: () => void;
  // True when a newer notification has arrived than the newest loaded row. Shows the "New
  // notifications" pill at the top of the list.
  hasNewNotifications: boolean;
  // Refreshes the list from the head. Invoked when the "New notifications" pill is clicked; the panel
  // also scrolls the list back to the top.
  onShowNew: () => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
  onRetry: () => void;
}

// Fire `onLoadMore` once the list is scrolled within this many pixels of the bottom, so the next page
// starts loading slightly before the user reaches the end rather than only at the exact bottom.
const LOAD_MORE_SCROLL_THRESHOLD_PX = 120;

/**
 * The notification inbox panel: header with a "Mark all read" affordance, an All/Unread tab switch,
 * and a scrollable list (or an empty state) of notification rows.
 */
export default function NotificationPanel({
  activeTab,
  onTabChange,
  notifications,
  unreadCount,
  isLoading,
  isError,
  hasMore,
  onLoadMore,
  hasNewNotifications,
  onShowNew,
  onMarkAllRead,
  onDismiss,
  onRetry,
}: NotificationPanelProps): JSX.Element {
  const {classes} = useStyles();
  const hasUnread = unreadCount > 0;

  const listRef = useRef<HTMLDivElement>(null);

  // A scroll sets the throbber; this effect clears it once the fetch settles. `isError` and
  // `activeTab` are deps because a failed page or a tab switch can leave `loadedCount`/`hasMore`
  // unchanged, which would otherwise strand the throbber on and block all later paging.
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadedCount = notifications.length;
  useEffect(() => {
    setIsLoadingMore(false);
  }, [loadedCount, hasMore, isError, activeTab]);

  const handleScroll = useCallback(() => {
    const list = listRef.current;
    if (list == null || !hasMore || isLoadingMore) {
      return;
    }
    const distanceFromBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distanceFromBottom <= LOAD_MORE_SCROLL_THRESHOLD_PX) {
      setIsLoadingMore(true);
      onLoadMore();
    }
  }, [hasMore, isLoadingMore, onLoadMore]);

  const handleShowNew = useCallback(() => {
    const list = listRef.current;
    if (list != null) {
      list.scrollTo({top: 0});
    }
    onShowNew();
  }, [onShowNew]);

  const showList = !isLoading && !isError && notifications.length > 0;

  return (
    <Box className={classes.panel} {...getTestProps({component: 'NotificationPanel', element: 'root'})}>
      <Group position="apart" align="center" className={classes.header}>
        <Title order={2}>Notifications</Title>
        <Button
          variant="ghost"
          size="xs"
          compact={true}
          disabled={!hasUnread || isLoading || isError}
          onClick={onMarkAllRead}
        >
          Mark all read
        </Button>
      </Group>
      <Box className={classes.tabRow}>
        <SegmentedControl
          size="xs"
          classNames={{control: classes.segmentedControl, label: classes.segmentedControlLabel}}
          value={activeTab}
          onChange={(value) => {
            onTabChange(value as NotificationTab);
          }}
          data={[
            {label: 'All', value: NotificationTab.ALL},
            {
              value: NotificationTab.UNREAD,
              label: (
                <Group spacing="xs" position="center" noWrap={true}>
                  <span>Unread</span>
                  {hasUnread && (
                    <Badge size="xs" variant="filled" radius="xl">
                      {unreadCount}
                    </Badge>
                  )}
                </Group>
              ),
            },
          ]}
        />
      </Box>
      <Box
        ref={listRef}
        className={classes.listArea}
        onScroll={handleScroll}
        {...getTestProps({component: 'NotificationPanel', element: 'listArea'})}
      >
        {hasNewNotifications && showList && (
          <Box className={classes.newPill}>
            <Button
              variant="primary"
              size="xs"
              radius="xl"
              compact={true}
              leftIcon={<BenchlingIcon icon="arrow-up" />}
              className={classes.newPillButton}
              onClick={handleShowNew}
            >
              New notifications
            </Button>
          </Box>
        )}
        {isLoading ? (
          <CenteredHorizontalThrobber />
        ) : isError ? (
          <ErrorState onRetry={onRetry} />
        ) : notifications.length === 0 ? (
          <EmptyState activeTab={activeTab} />
        ) : (
          <>
            {notifications.map((notification) => (
              <NotificationRow key={notification.id} notification={notification} onDismiss={onDismiss} />
            ))}
            {isLoadingMore && (
              <Box
                className={classes.loadingMore}
                {...getTestProps({component: 'NotificationPanel', element: 'loadingMore'})}
              >
                <CenteredHorizontalThrobber />
              </Box>
            )}
            {!hasMore && !isLoadingMore && (
              <Box
                className={classes.endOfList}
                {...getTestProps({component: 'NotificationPanel', element: 'endOfList'})}
              >
                <Text size="sm" color="dimmed">
                  {activeTab === NotificationTab.UNREAD
                    ? 'No more unread notifications'
                    : 'No more notifications'}
                </Text>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
